import { FC } from 'react';
import { Grid, ReadOnlyField, Typography, Well } from '@oresundsbron/bridge-ui';
import { cx } from 'class-variance-authority';
import { VehicleInfo } from '~/components/Forms/RegisterAlpr/types';
import { pipe } from 'fp-ts/lib/function';
import { getOrElse, map } from 'fp-ts/lib/Option';
import { findFirst } from 'fp-ts/lib/Array';
import { Eq } from 'fp-ts/lib/string';

export type VehicleDataLabels = {
  make: string;
  model: string;
  color: string;
  fuelType: string;
  emissionClass: string;
  hybrid: string;
  weightClass: string;
  weightClasses: {
    heavy: string;
    light: string;
  };
  hybridLabel: {
    true: string;
    false: string;
  };
  vehicleInformationTitle: string;
};

export const VehicleInformationDisplay: FC<{
  data: VehicleInfo;
  labels: VehicleDataLabels;
  className?: string;
  extraData: {
    vehicleColors: { label: string; value: string }[];
    fuelTypes: { label: string; value: string }[];
    emissionClasses: { label: string; value: string }[];
    defaultVehicleColor: string;
  };
}> = ({
  labels,
  data,
  className,
  extraData: { vehicleColors, defaultVehicleColor, fuelTypes, emissionClasses },
}) => (
  <Well className="col-span-full md:col-span-12" transparent={false}>
    <div className="grid grid-cols-2 gap-4 md:auto-cols-auto md:grid-flow-col md:grid-cols-none">
      <ReadOnlyField label={labels.make}>{data.make}</ReadOnlyField>
      <ReadOnlyField label={labels.model}>{data.model}</ReadOnlyField>
      <ReadOnlyField label={labels.color}>
        {pipe(
          vehicleColors,
          findFirst((c) => Eq.equals(c.value, data.color)),
          map((c) => c.label),
          getOrElse(() => defaultVehicleColor)
        )}
      </ReadOnlyField>
      <ReadOnlyField label={labels.weightClass}>
        {labels.weightClasses?.[data.isHGV ? 'heavy' : 'light']}
      </ReadOnlyField>
      <ReadOnlyField label={labels.fuelType}>
        {pipe(
          fuelTypes,
          findFirst((f) => Eq.equals(f.value, data.fuelTypeId.toString())),
          map((f) => f.label),
          getOrElse(() => '')
        )}
      </ReadOnlyField>
      <ReadOnlyField label={labels.emissionClass}>
        {pipe(
          emissionClasses,
          findFirst((e) => Eq.equals(e.value, data.euroEmissionNorm ?? '')),
          map((e) => e.label),
          getOrElse(() => '')
        )}
      </ReadOnlyField>
      <ReadOnlyField label={labels.hybrid}>
        {!!data.isHybrid ? labels.hybridLabel.true : labels.hybridLabel.false}
      </ReadOnlyField>
    </div>
  </Well>
);
